import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { animateScroll } from 'react-scroll';

import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Label,
  Row,
  TabContent,
  TabPane,
  Alert
} from 'reactstrap';
import CredsModal from '../Vendor/CredsModal';
import SummaryModal from '../Vendor/Modal';

import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import BZAPIService from '../../../services/BZAPIService';
import DefaultConfigTab from '../section/DefaultConfigTab';
import ProductPriority from '../section/ProductPriority';
import ExecutionStatsTab from '../section/ExecutionStatsTab';
import { useScheduling } from '../../../hooks/useScheduling';
// constants
import SETTINGS from '../../../constants/settings';
import ButtonIcon from '../../common/ButtonIcon';
import { FieldGroup } from '../../../libs/formLib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ConfigFormAwsResilienceHub = ({
  isReadOnly,
  vendor,
  vendorLabel,
  bugsDateBack,
  setBugsDateBack,
  vendorId,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  vendorEventScopes,
  vendorResolutions,
  setVendorResolutions,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  vendorBugCategories,
  setVendorBugCategories,
  vendorBugTypeOptions,
  vendorBugTypes,
  setVendorBugTypes,
  vendorMitigationTypes,
  setVendorMitigationTypes,
  vendorMitigationTypeOptions,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated,
  excludedApps,
  setExcludedApps,
  resiliencyData
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [isValidCredentials, setIsValidCredentials] = useState(false);
  const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);
  const [isUpdatingCreds, setIsUpdatingCreds] = useState(false);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  const [appOptions, setAppOptions] = useState([]);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveAWSArn() {
    const data = {
      secret: {
        awsResilienceHubApiRoleArn: fields.awsResilienceHubApiRoleArn
      },
      vendorId: 'aws',
      key: 'resilienceHubArn'
    };
    // cloud apps secret
    return BZAPIService.createOrUpdateAWSSecret(data);
  }

  async function testCreds() {
    setIsTestingValidCredentials(true);
    const data = {
      awsResilienceHubApiRoleArn: fields.awsResilienceHubApiRoleArn
    };
    const result = await BZAPIService.vendorTestCreds(vendorId, data);
    setIsTestingValidCredentials(false);
    if (result) {
      // valid creds
      if (!result.roleAssumed) return toast.error(`Unable to assume role.`);
      if (!result.resilienceHubApiAccess) return toast.error(`Unable to access AWS Resilience Hub API.`);

      // Handle app names from the API response
      if (result.resHubAppNames) {
        // Transform string array into array of objects with value and label properties
        const formattedAppOptions = result.resHubAppNames.map(app => ({
          value: app,
          label: app
        }));
        setAppOptions(formattedAppOptions);
      }

      // Clear excluded apps
      setExcludedApps([]);

      setIsValidCredentials(true);
      setIsAddingCredentials(false);
      setIsUpdatingCreds(true);
      toast.success(`AWS Resilience Hub Role ARN validated.`);
    } else {
      toast.error(`Unable to validate AWS Resilience Hub Role ARN.`);
    }
  }

  async function saveConfig(serviceSecret) {
    // save client config
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      secretId: serviceSecret.Name,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      vendorStatuses,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields,
      excludedApps: excludedApps.map(app => app.value),
      vendorMitigationTypes
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun, isUpdatingCreds);
  }

  function cancelCreds() {
    // fields.awsResilienceHubApiRoleArn = '';
    setIsAddingCredentials(false);
  }

  async function handleSave() {
    try {
      setIsSubmitted(true);
      setIsLoading(true);

      // Validate
      if (!fields.secretId) {
        if (!fields.awsResilienceHubApiRoleArn) {
          console.log('misisng secret');
          setValidated(false);
          setActiveTab('2');
          setIsLoading(false);
          return;
        }
      }
      if (!bugsDateBack.length || !snCompanySysId) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }

      if (!vendorMitigationTypes || vendorMitigationTypes.length === 0) {
        console.log('missing mitigation types');
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        toast.error('Please select at least one mitigation type');
        return;
      }

      let serviceSecret;

      if (fields.awsResilienceHubApiRoleArn) {
        // Updated secrets
        serviceSecret = await saveAWSArn();
      } else {
        // use existing secrets
        serviceSecret = { Name: fields.secretId };
      }

      if (!serviceSecret || !serviceSecret.Name) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('2');
        throw Error('Missing Secret');
      }

      await saveConfig(serviceSecret);
      fields.secretId = serviceSecret.Name;
      // set updated secretId
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setIsUpdatingCreds(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
    // window.scrollTo(0, 0);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority) &&
      (fields.secretId || fields.awsResilienceHubApiRoleArn) &&
      vendorMitigationTypes?.length > 0
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
  }, [
    fields,
    bugsDateBack,
    priorityStats,
    vendorStatuses,
    vendorEventScopes,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId,
    vendorMitigationTypes,
    setAllRequired,
    setIsUpdated
  ]);
  console.log('resiliencyData', resiliencyData);
  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={true}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
          additionalGraphHeading="AWS Resilience Hub Metrics"
          additionalGraphs={resiliencyData}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="2">
            {activeTab === '2' && (
              <Card bg={''} text={'dark'} className="mb-4">
                <CardBody>
                  <Row form>
                    <Col lg="12">
                      <FormGroup>
                        <Label>
                          AWS Resilience Hub Role ARN
                          <span style={{ color: '#bd472a' }}>*</span>
                        </Label>
                        {!isAddingCredentials ? (
                          <>
                            <div>
                              <ButtonIcon
                                onClick={() => setIsAddingCredentials(true)}
                                color="falcon-default"
                                size="sm"
                                icon="plus"
                                iconClassName="fs--2"
                              >
                                Set Credentials
                              </ButtonIcon>

                              {isValidCredentials || fields.secretId ? (
                                <>
                                  {'  '}
                                  <FontAwesomeIcon icon={'check'} style={{ color: 'green', marginLeft: '10px' }} /> AWS
                                  Resilience Hub Role ARN Stored
                                </>
                              ) : null}
                            </div>
                          </>
                        ) : (
                          <>
                            <Row>
                              <Col lg="6">
                                <FieldGroup
                                  type="text"
                                  id="awsResilienceHubApiRoleArn"
                                  onChange={handleFieldChange}
                                  value={fields.awsResilienceHubApiRoleArn}
                                  showFeedback={!isValidCredentials}
                                  feedback={'AWS Resilience Hub Role ARN has not been validated or is invalid.'}
                                  placeholder="Enter AWS Resilience Hub Role ARN"
                                  style={{ marginBottom: '10px' }}
                                  required
                                />
                              </Col>
                            </Row>
                            <CredsModal
                              isTestingValidCredentials={isTestingValidCredentials}
                              setIsTestingCredentials={setIsTestingValidCredentials}
                              fields={fields}
                              testCreds={testCreds}
                              vendorId={vendorId}
                            />
                            <Button
                              onClick={cancelCreds}
                              color="falcon-danger"
                              size="md"
                              iconclassname="fs--2"
                              style={{ marginRight: '10px' }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        <FormFeedback
                          className={
                            !fields.secretId && !fields.awsResilienceHubApiRoleArn && isSubmitted ? 'd-block' : ''
                          }
                          type="invalid"
                        >
                          <Alert color="danger">Please specify credentials.</Alert>
                        </FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                  {/* TODO if setting has excludedApps ['item1', 'item2'] display them*/}
                  {!appOptions.length && (
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <Label>Excluded Applications</Label>
                          <div>
                            {excludedApps.length > 0 ? (
                              excludedApps.map(app => (
                                <div key={app} className="badge badge-light mr-2">
                                  {app}
                                </div>
                              ))
                            ) : (
                              <div className="text-muted">No applications excluded.</div>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                  {/* App Selection Section */}
                  {(isValidCredentials || fields.secretId) && !!appOptions.length && (
                    <Row form>
                      <Col lg="12">
                        <FormGroup>
                          <Label>Exclude Apps From Resilience Hub Monitoring</Label>
                          <Select
                            closeMenuOnSelect={false}
                            components={makeAnimated()}
                            isMulti
                            options={appOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={selectedOptions => setExcludedApps(selectedOptions || [])}
                            value={excludedApps}
                            placeholder="Select applications to exclude..."
                            isDisabled={!appOptions.length}
                          />
                          {!appOptions.length && (
                            <div className="text-muted mt-2">
                              <small>Please validate your AWS credentials to set excluded applications.</small>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>
            )}
          </TabPane>

          <DefaultConfigTab
            vendor={vendor}
            activeTab={activeTab}
            vendorLabel={vendorLabel}
            bugsDateBack={bugsDateBack}
            setBugsDateBack={setBugsDateBack}
            isSubmitted={isSubmitted}
            snCompanySysIdErrors={snCompanySysIdErrors}
            snCompanySysIdOptions={snCompanySysIdOptions}
            setSnCompanySysId={setSnCompanySysId}
            snCompanySysId={snCompanySysId}
            fields={fields}
            handleFieldChange={handleFieldChange}
          />

          <TabPane tabId="4">
            {activeTab === '4' && (
              <>
                <ProductPriority
                  title="Default Bug Configuration"
                  vendorPriorityOptions={vendorPriorityOptions}
                  vendorStatusOptions={vendorStatusOptions}
                  vendorResolutionOptions={vendorResolutionOptions}
                  setVendorStatuses={setVendorStatuses}
                  vendorStatuses={vendorStatuses}
                  isSubmitted={isSubmitted}
                  priorityMaps={priorityMaps}
                  setVendorResolutions={setVendorResolutions}
                  vendorResolutions={vendorResolutions}
                  setPriorityMaps={setPriorityMaps}
                  snPriorityOptions={snPriorityOptions}
                  keywords={keywords}
                  setVendorBugTypes={setVendorBugTypes}
                  vendorBugTypes={vendorBugTypes}
                  vendorBugTypeOptions={vendorBugTypeOptions}
                  setKeywords={setKeywords}
                  searchFields={searchFields}
                  vendorBugCategories={vendorBugCategories}
                  setVendorBugCategories={setVendorBugCategories}
                  setSearchFields={setSearchFields}
                  vendorMitigationTypes={vendorMitigationTypes}
                  setVendorMitigationTypes={setVendorMitigationTypes}
                  vendorMitigationTypeOptions={vendorMitigationTypeOptions}
                />
              </>
            )}
          </TabPane>
          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>
          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormAwsResilienceHub.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  vendorEventScopeOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  vendorBugTypes: PropTypes.array,
  setVendorBugTypes: PropTypes.func,
  isServiceActive: PropTypes.number,
  organizations: PropTypes.array,
  vendorBugCategories: PropTypes.array,
  vendorBugCategoryOptions: PropTypes.array,
  vendorBugTypeOptions: PropTypes.array,
  vendorMitigationTypes: PropTypes.array,
  setVendorMitigationTypes: PropTypes.func,
  vendorMitigationTypeOptions: PropTypes.array,
  vendorComplianceStatus: PropTypes.array,
  setVendorComplianceStatus: PropTypes.func,
  vendorComplianceStatusOptions: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  setOrganizations: PropTypes.func,
  organizationOptions: PropTypes.array,
  setOrganizationOptions: PropTypes.func,
  awsOrgHealthEnabled: PropTypes.bool,
  setAwsOrgHealthEnabled: PropTypes.func,
  awsOrgHealthSupport: PropTypes.bool,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func,
  excludedApps: PropTypes.array,
  setExcludedApps: PropTypes.func,
  resiliencyData: PropTypes.array
};
export default ConfigFormAwsResilienceHub;
